import mathUtils from 'utils/mathUtils'


const PLANETS_RADII = {
    "earth": 6371.0088,
    "mars": 3389.5
};


export default function convert(conversion, rootField, data, changedField, changedValue, parameters, t) {
    function getField(expression) {
        let currentField = rootField;
        if (expression.startsWith('../')) {
            while (expression.startsWith('../')) {
                currentField = currentField.substring(0, currentField.lastIndexOf('.'));
                expression = expression.substring(3);
            }
        } else if (expression.startsWith('./')) {
            expression = expression.substring(2);
        } else if (expression.startsWith('/')) {
            currentField = "";
            expression = expression.substring(1);
        }
        return currentField + (currentField && expression ? "." : "") + expression;
    }

    function getValue(expression) {
        const currentField = getField(expression);
        return currentField === changedField ? changedValue : data[currentField];
    }

    let relativeChangedField = null;
    if (changedField) {
        if (rootField) {
            if (!changedField.startsWith(rootField + ".")) {
                throw new Error(`Conversion: rootField ${rootField} is not start of changedField ${changedField}!`);
            }
            relativeChangedField = changedField.substring(rootField.length + 1);
        } else {
            relativeChangedField = changedField;
        }
    }

    const precisions = {
        "semimajor-axis": 3,
        "periapsis-height": 3,
        "apoapsis-height": 3,
        "eccentricity": 7,
        "gain": 3,
        "gain-db": 3,
        "power": 3,
        "power-dbm": 3,
        "sensitivity": 3,
        "sensitivity-dbm": 3
    };

    switch (conversion) {
        case "axis-eccentricity/periapsis-apoapsis":
            const R = PLANETS_RADII[parameters.planet] || PLANETS_RADII['earth'];
            if (!relativeChangedField || ["semimajor-axis", "eccentricity"].includes(relativeChangedField)) {
                // axis-eccentricity group
                const semimajorAxis = getValue("semimajor-axis");
                const eccentricity = getValue("eccentricity");
                return {
                    [getField("periapsis-height")]: mathUtils.toPrecision((1 - eccentricity) * semimajorAxis - R, precisions["periapsis-height"], t),
                    [getField("apoapsis-height")]: mathUtils.toPrecision((1 + eccentricity) * semimajorAxis - R, precisions["apoapsis-height"], t)
                };
            } else if (["periapsis-height", "apoapsis-height"].includes(relativeChangedField)) {
                // periapsis-apoapsis group
                const periapsisHeight = getValue("periapsis-height");
                const apoapsisHeight = getValue("apoapsis-height");
                const semimajorAxis = (periapsisHeight + apoapsisHeight)/2 + R;
                return {
                    [getField("semimajor-axis")]: mathUtils.toPrecision(semimajorAxis, precisions["semimajor-axis"], t),
                    [getField("eccentricity")]: mathUtils.toPrecision((apoapsisHeight - periapsisHeight) / (2 * semimajorAxis), precisions["eccentricity"], t)
                };
            }
            break;
        case "gain/gain-db":
            if (!relativeChangedField || ["gain"].includes(relativeChangedField)) {
                const gain = getValue("gain");
                return {
                    [getField("gain-db")]: mathUtils.toPrecision(10 * Math.log10(gain), precisions["gain-db"], t)
                };
            } else if (["gain-db"].includes(relativeChangedField)) {
                const gainDb = getValue("gain-db");
                return {
                    [getField("gain")]: mathUtils.toPrecision(Math.pow(10, gainDb / 10), precisions["gain"], t)
                };
            }
            break;
        case "power/power-dbm":
            if (!relativeChangedField || ["power"].includes(relativeChangedField)) {
                const power = getValue("power");
                return {
                    [getField("power-dbm")]: mathUtils.toPrecision(10 * Math.log10(power) + 30, precisions["power-dbm"], t)
                };
            } else if (["power-dbm"].includes(relativeChangedField)) {
                const powerDbm = getValue("power-dbm");
                return {
                    [getField("power")]: mathUtils.toPrecision(Math.pow(10, (powerDbm - 30) / 10), precisions["power"], t)
                };
            }
            break;
        case "minimum-output-power/minimum-output-power-dbm":
            if (!relativeChangedField || ["minimum-output-power"].includes(relativeChangedField)) {
                const power = getValue("minimum-output-power");
                return {
                    [getField("minimum-output-power-dbm")]: mathUtils.toPrecision(10 * Math.log10(power) + 30, precisions["power-dbm"], t)
                };
            } else if (["minimum-output-power-dbm"].includes(relativeChangedField)) {
                const powerDbm = getValue("minimum-output-power-dbm");
                return {
                    [getField("minimum-output-power")]: mathUtils.toPrecision(Math.pow(10, (powerDbm - 30) / 10), precisions["power"], t)
                };
            }
            break;
        case "maximum-output-power/maximum-output-power-dbm":
            if (!relativeChangedField || ["maximum-output-power"].includes(relativeChangedField)) {
                const power = getValue("maximum-output-power");
                return {
                    [getField("maximum-output-power-dbm")]: mathUtils.toPrecision(10 * Math.log10(power) + 30, precisions["power-dbm"], t)
                };
            } else if (["maximum-output-power-dbm"].includes(relativeChangedField)) {
                const powerDbm = getValue("maximum-output-power-dbm");
                return {
                    [getField("maximum-output-power")]: mathUtils.toPrecision(Math.pow(10, (powerDbm - 30) / 10), precisions["power"], t)
                };
            }
            break;
        case "sensitivity/sensitivity-dbm":
            if (!relativeChangedField || ["sensitivity"].includes(relativeChangedField)) {
                const sensitivity = getValue("sensitivity");
                return {
                    [getField("sensitivity-dbm")]: mathUtils.toPrecision(10 * Math.log10(sensitivity) + 30, precisions["sensitivity-dbm"], t)
                };
            } else if (["sensitivity-dbm"].includes(relativeChangedField)) {
                const sensitivityDbm = getValue("sensitivity-dbm");
                return {
                    [getField("sensitivity")]: mathUtils.toPrecision(Math.pow(10, (sensitivityDbm - 30) / 10), precisions["sensitivity"], t)
                };
            }
            break;
        case "minimum-sensitivity/minimum-sensitivity-dbm":
            if (!relativeChangedField || ["minimum-sensitivity"].includes(relativeChangedField)) {
                const sensitivity = getValue("minimum-sensitivity");
                return {
                    [getField("minimum-sensitivity-dbm")]: mathUtils.toPrecision(10 * Math.log10(sensitivity) + 30, precisions["sensitivity-dbm"], t)
                };
            } else if (["minimum-sensitivity-dbm"].includes(relativeChangedField)) {
                const sensitivityDbm = getValue("minimum-sensitivity-dbm");
                return {
                    [getField("minimum-sensitivity")]: mathUtils.toPrecision(Math.pow(10, (sensitivityDbm - 30) / 10), precisions["sensitivity"], t)
                };
            }
            break;
        case "maximum-sensitivity/maximum-sensitivity-dbm":
            if (!relativeChangedField || ["maximum-sensitivity"].includes(relativeChangedField)) {
                const sensitivity = getValue("maximum-sensitivity");
                return {
                    [getField("maximum-sensitivity-dbm")]: mathUtils.toPrecision(10 * Math.log10(sensitivity) + 30, precisions["sensitivity-dbm"], t)
                };
            } else if (["maximum-sensitivity-dbm"].includes(relativeChangedField)) {
                const sensitivityDbm = getValue("maximum-sensitivity-dbm");
                return {
                    [getField("maximum-sensitivity")]: mathUtils.toPrecision(Math.pow(10, (sensitivityDbm - 30) / 10), precisions["sensitivity"], t)
                };
            }
            break;
        default:
            throw new Error(`Unknown conversion ${conversion}!`);
    }

    return {};
}